<template>
  <div class="container mt-4">
    <div class="card p-4 shadow-sm">
      <div class="d-flex align-items-center mb-4">
        <button class="btn btn-outline-secondary me-3" @click="volver">← Regresar</button>
        <h3 class="m-0 flex-grow-1 text-center">Cierre de Caja por Fechas</h3>
        <button class="btn btn-outline-primary ms-auto me-2" @click="imprimirCierre" :disabled="!cierre">
          🖨 Imprimir Cierre
        </button>
        <button class="btn btn-outline-secondary" @click="descargarFormatoCorte" :disabled="!fechaInicio || !fechaFin">
          📄 PDF Formato Corte
        </button>
      </div>

      <!-- Filtros -->
      <div class="row g-2 align-items-end mb-3">
        <div class="col-auto">
          <label class="form-label m-0 small text-muted">Fecha Inicio</label>
          <input type="date" v-model="fechaInicio" class="form-control form-control-sm" style="max-width:170px" @change="cargarCierre" />
        </div>
        <div class="col-auto">
          <label class="form-label m-0 small text-muted">Hora</label>
          <input type="time" v-model="horaInicio" class="form-control form-control-sm" style="max-width:120px" @change="cargarCierre" />
        </div>
        <div class="col-auto">
          <label class="form-label m-0 small text-muted">Fecha Fin</label>
          <input type="date" v-model="fechaFin" class="form-control form-control-sm" style="max-width:170px" @change="cargarCierre" />
        </div>
        <div class="col-auto">
          <label class="form-label m-0 small text-muted">Hora</label>
          <input type="time" v-model="horaFin" class="form-control form-control-sm" style="max-width:120px" @change="cargarCierre" />
        </div>
        <div class="col-md-3">
          <label class="form-label m-0 small text-muted">Filtrar por método de pago</label>
          <div class="border rounded p-2 bg-white" style="max-height:120px;overflow-y:auto;min-width:200px">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="metodo-todos" :checked="metodosPagoSeleccionados.length === 0" @change="toggleTodos" />
              <label class="form-check-label small fw-bold" for="metodo-todos">Todos</label>
            </div>
            <div v-for="m in metodosDisponibles" :key="m.IdMetodoPago" class="form-check">
              <input class="form-check-input" type="checkbox" :id="'metodo-' + m.IdMetodoPago" :value="m.IdMetodoPago" v-model="metodosPagoSeleccionados" @change="cargarCierre" />
              <label class="form-check-label small" :for="'metodo-' + m.IdMetodoPago">
                {{ m.Metodo }}
                <span class="badge ms-1" :class="m.UsoMetodo == 1 ? 'bg-primary' : 'bg-secondary'" style="font-size:0.65rem">{{ m.UsoMetodo == 1 ? 'Fact.' : 'No Fact.' }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Filtro activo -->
      <div v-if="cierre?.filtro_label && cierre.filtro_label !== 'Todos los métodos de pago'" class="alert alert-info py-2 mb-3 small">
        <i class="bi bi-funnel me-1"></i> {{ cierre.filtro_label }}
      </div>

      <div v-if="cierre">
        <ul class="list-group mb-3">
          <li class="list-group-item"><strong>Fecha Inicio:</strong> {{ formatDate(cierre.fecha_inicio) }}</li>
          <li class="list-group-item"><strong>Fecha Fin:</strong> {{ formatDate(cierre.fecha_fin) }}</li>
          <li class="list-group-item"><strong>Huespedes Ingresados:</strong> {{ cierre.huespedes_ingresados }}</li>
        </ul>

        <h5 class="mb-2">Ingresos</h5>
        <ul class="list-group mb-4">
          <li class="list-group-item d-flex justify-content-between">
            <span>Hospedaje</span>
            <strong>Q{{ Number(cierre.ingresos.hospedaje).toFixed(2) }}</strong>
          </li>
          <li class="list-group-item d-flex justify-content-between">
            <span>Servicios Extra</span>
            <strong>Q{{ Number(cierre.ingresos.servicios_extra).toFixed(2) }}</strong>
          </li>
          <li class="list-group-item d-flex justify-content-between">
            <span>Restaurante</span>
            <strong>Q{{ Number(cierre.ingresos.restaurante).toFixed(2) }}</strong>
          </li>
          <li class="list-group-item d-flex justify-content-between bg-light">
            <span><strong>Total Ingresos</strong></span>
            <strong class="text-primary">Q{{ Number(cierre.ingresos.total_general).toFixed(2) }}</strong>
          </li>
        </ul>

        <h5 class="mb-2">Pagos</h5>
        <div v-if="Object.keys(cierre.pagos).length > 0">
          <div v-for="(grupo, uso) in cierre.pagos" :key="uso" class="mb-3">
            <h6 :class="uso === 'Facturado' ? 'text-primary' : 'text-secondary'">{{ uso }}</h6>
            <ul class="list-group">
              <li v-for="(cantidad, metodo) in grupo" :key="metodo" class="list-group-item d-flex justify-content-between">
                <span>{{ metodo }}</span>
                <strong>Q{{ Number(cantidad).toFixed(2) }}</strong>
              </li>
            </ul>
          </div>
        </div>
        <div v-else class="alert alert-secondary">No hay pagos registrados para estas fechas.</div>

        <h5 class="mb-2">Total Pagos</h5>
        <ul class="list-group mb-4">
          <li class="list-group-item d-flex justify-content-between">
            <span>Total Facturado</span>
            <strong>Q{{ Number(cierre.resumen_pagos.total_facturado).toFixed(2) }}</strong>
          </li>
          <li class="list-group-item d-flex justify-content-between">
            <span>Total Sin factura</span>
            <strong>Q{{ Number(cierre.resumen_pagos.total_no_facturado).toFixed(2) }}</strong>
          </li>
          <li class="list-group-item d-flex justify-content-between">
            <span>Cupones Aplicados</span>
            <strong>Q{{ Number(cierre.resumen_pagos.cupones_aplicados).toFixed(2) }}</strong>
          </li>
          <li class="list-group-item d-flex justify-content-between bg-light">
            <span><strong>Total Pagos</strong></span>
            <strong class="text-success">Q{{ Number(cierre.resumen_pagos.total_pagos).toFixed(2) }}</strong>
          </li>
        </ul>

        <h5 class="mb-2">Estado del Cuadre</h5>
        <ul class="list-group">
          <li class="list-group-item d-flex justify-content-between">
            <span>Diferencia</span>
            <strong :class="cierre.estado_cuadre === 'Saldo pendiente' ? 'text-danger' : 'text-success'">
              Q{{ Number(cierre.diferencia).toFixed(2) }}
            </strong>
          </li>
          <li class="list-group-item d-flex justify-content-between">
            <span>Estado</span>
            <span :class="{
              'badge bg-success': cierre.estado_cuadre === 'Cuadre exacto',
              'badge bg-warning text-dark': cierre.estado_cuadre === 'Saldo pendiente',
              'badge bg-info': cierre.estado_cuadre === 'Saldo a favor',
            }">{{ cierre.estado_cuadre }}</span>
          </li>
        </ul>
      </div>

      <div v-else-if="cargando" class="text-center text-muted my-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3">Cargando cierre de caja...</p>
      </div>
      <div v-else class="text-center text-muted my-5">
        <p>Seleccione un rango de fechas para generar el cierre.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/config/axios";
import { API_URL } from "@/config/config.js";
import dayjs from "dayjs";

export default {
  name: "CierreCajaPorFechas",
  data() {
    return {
      cierre: null,
      cargando: false,
      fechaInicio: "",
      horaInicio: "00:00",
      fechaFin: "",
      horaFin: "23:59",
      metodosDisponibles: [],
      metodosPagoSeleccionados: [],
    };
  },
  async mounted() {
    const today    = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    this.fechaInicio = firstDay.toLocaleDateString("fr-CA");
    this.fechaFin    = today.toLocaleDateString("fr-CA");
    await Promise.all([this.cargarMetodos(), this.cargarCierre()]);
  },
  methods: {
    formatDate(date) {
      return dayjs(date).format("DD/MM/YYYY");
    },
    volver() {
      this.$router.push("/inicio");
    },
    toggleTodos() {
      this.metodosPagoSeleccionados = [];
      this.cargarCierre();
    },
    metodosCsv() {
      return this.metodosPagoSeleccionados.length > 0
        ? this.metodosPagoSeleccionados.join(',')
        : 'todos';
    },
    async cargarMetodos() {
      try {
        const { data } = await axios.get(`${API_URL}/metodos-pago/cierre`);
        this.metodosDisponibles = data;
      } catch {
        this.metodosDisponibles = [];
      }
    },
    async cargarCierre() {
      if (!this.fechaInicio || !this.fechaFin) return;
      this.cierre   = null;
      this.cargando = true;
      try {
        const res = await axios.get(`${API_URL}/cierre-caja-por-fechas`, {
          params: {
            fecha_inicio:  this.fechaInicio,
            hora_inicio:   this.horaInicio,
            fecha_fin:     this.fechaFin,
            hora_fin:      this.horaFin,
            metodos_pago:  this.metodosCsv(),
          },
        });
        this.cierre = res.data;
      } catch (error) {
        console.error("Error al cargar cierre de caja:", error);
        alert("Error al cargar el cierre de caja: " + (error.response?.data?.message || error.message));
      } finally {
        this.cargando = false;
      }
    },
    async imprimirCierre() {
      if (!this.fechaInicio || !this.fechaFin) return;
      try {
        const response = await axios.get(`${API_URL}/cierre-caja-por-fechas-pdf`, {
          params: {
            fecha_inicio: this.fechaInicio,
            hora_inicio:  this.horaInicio,
            fecha_fin:    this.fechaFin,
            hora_fin:     this.horaFin,
            metodos_pago: this.metodosCsv(),
          },
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href  = window.URL.createObjectURL(blob);
        link.download = `Cierre_Caja_${this.fechaInicio}_${this.fechaFin}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error descargando PDF:", error);
      }
    },
    async descargarFormatoCorte() {
      if (!this.fechaInicio || !this.fechaFin) return;
      try {
        const response = await axios.get(`${API_URL}/cierre-caja-formato-corte-pdf`, {
          params: {
            fecha_inicio: this.fechaInicio,
            hora_inicio:  this.horaInicio,
            fecha_fin:    this.fechaFin,
            hora_fin:     this.horaFin,
          },
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href  = window.URL.createObjectURL(blob);
        link.download = `Corte_Caja_${this.fechaInicio}_${this.fechaFin}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error descargando PDF de formato corte:", error);
      }
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
  text-align: center;
}
</style>
